import React, { useState, useEffect } from "react";
import { duprExchangeCode } from "../firebase";

// Client key (ei salainen — ainoastaan key, ei secret)
const DUPR_CLIENT_KEY = "ck-1cd233d9-8b2a-4ef1-f8c8-c6ce2631a016";
const DUPR_IFRAME_BASE = "https://dashboard.dupr.com/login-external-app";

export interface DuprConnectState {
  duprId?: string;
  fullName?: string;
  singlesRating?: number | null;
  doublesRating?: number | null;
  hasBasicL1?: boolean;
  duprEntitlements?: string[];
  /** Oman kirjautumisen aikaleima. Puuttuu, jos vain tunnus on syötetty. */
  linkedAt?: string;
}

interface Props {
  /** Spinloop-pelaajan Firestore-ID */
  playerId: string;
  /** Klubin Firestore-ID — tarvitaan tallennukseen */
  clubId: string;
  /** Pelaajan nimi (näytetään statuksessa) */
  playerName: string;
  /** Nykyinen DUPR-tila jos jo linkitetty */
  current?: DuprConnectState;
  /** Kutsutaan kun yhteys on valmis (tai poistettu) */
  onConnected?: (state: DuprConnectState | null) => void;
  /** Tumma vai vaalea tyyli */
  dark?: boolean;
  /** Kieliasetus */
  lang?: "fi" | "en";
}

/**
 * "Yhdistä DUPR-tili" -nappi.
 * Avaa iframe-modaalin DUPR-kirjautumiselle. DUPR lähettää postMessage
 * jossa userToken + duprId kun käyttäjä on kirjautunut ja antanut suostumuksen.
 */
const DuprConnectButton: React.FC<Props> = ({
  playerId, clubId, playerName: _playerName, current, onConnected, dark = false, lang = "fi",
}) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Yhdistetty vasta, kun pelaaja on itse kirjautunut DUPR:iin tämän
  // kautta. Pelkkä tunnus (ylläpitäjän syöttämä tai vanha linkitys) ei
  // riitä: DUPR ei luovuta ratingia eikä ottelut mene DUPR:iin.
  const isConnected = !!current?.duprId && !!current?.linkedAt;
  const idOnly = !!current?.duprId && !current?.linkedAt;

  // Base64-enkoodattu client key iframe-URL:iin
  const iframeSrc = `${DUPR_IFRAME_BASE}/${btoa(DUPR_CLIENT_KEY)}`;

  useEffect(() => {
    if (!showModal) return;

    const handler = async (event: MessageEvent) => {
      // Hyväksy DUPR:n domaineilta tulevat viestit (prod + uat)
      if (!event.origin.includes("dupr.com") && !event.origin.includes("dupr.gg")) return;

      const data = event.data as Record<string, unknown> | null;
      if (!data) return;

      const userToken = data.userToken as string | undefined;
      const duprId    = (data.duprId ?? data.userId ?? data.id) as string | number | undefined;

      // Kenttien NIMET, ei arvoja. Tokenia ei kirjoiteta konsoliin.
      //
      // Syy: entitlementin ja klubiroolin tarkistus lahetyshetkella vaatii
      // pelaajan tokenin myos SSO:n jalkeen, eika meilla ole tietoa
      // lahettaako DUPR refresh tokenin tassa viestissa. Tama rivi kertoo
      // sen ensimmaisesta oikeasta yhdistamisesta. Ks. docs/STATE.md.
      console.log("[DUPR postMessage]", {
        origin: event.origin,
        keys: Object.keys(data),
        userToken: !!userToken,
        duprId,
      });

      if (!userToken || !duprId) return;

      setLoading(true);
      setError(null);

      try {
        const result = await duprExchangeCode({
          userToken,
          duprId: String(duprId).trim(),
          playerId,
          clubId,
        });
        setShowModal(false);
        onConnected?.(result.data as DuprConnectState);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        setLoading(false);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [showModal, playerId, clubId, onConnected]);

  const disconnect = () => onConnected?.(null);

  const textColor = dark ? "#eee" : "#163b68";
  const mutedColor = dark ? "#888" : "#6e84a3";
  const cardBg = dark ? "#2a2a2a" : "#f0f4f9";
  const cardBorder = dark ? "#444" : "#c8d6e5";

  if (isConnected) {
    return (
      <div style={{
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: 10, padding: "12px 14px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 16 }}>✅</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: textColor }}>
              {lang === "en" ? "DUPR connected" : "DUPR yhdistetty"}
            </span>
          </div>
          <div style={{ fontSize: 11, color: mutedColor, marginTop: 2 }}>
            {current?.fullName && <span>{current.fullName} · </span>}
            <span style={{ fontFamily: "monospace", letterSpacing: 2 }}>{current?.duprId}</span>
          </div>
          {(current?.singlesRating || current?.doublesRating) && (
            <div style={{ fontSize: 11, color: mutedColor, marginTop: 2 }}>
              S: {current.singlesRating?.toFixed(3) ?? "NR"} · D: {current.doublesRating?.toFixed(3) ?? "NR"}
            </div>
          )}
          {/* DUPR taso */}
          <div style={{ marginTop: 4 }}>
            {current?.duprEntitlements?.includes("PREMIUM_L1") ? (
              <span style={{ fontSize: 10, fontWeight: 700, color: "#7c3aed", background: "#f5f3ff", borderRadius: 4, padding: "2px 7px" }}>
                ⭐ DUPR+ (Premium)
              </span>
            ) : current?.hasBasicL1 === false ? (
              <span style={{ fontSize: 10, fontWeight: 700, color: "#b45309", background: "#fffbeb", borderRadius: 4, padding: "2px 7px" }}>
                ⚠️ {lang === "en" ? "No entitlement" : "Ei oikeutta"}
              </span>
            ) : current?.hasBasicL1 === true ? (
              <span style={{ fontSize: 10, fontWeight: 700, color: "#16a34a", background: "#f0fdf4", borderRadius: 4, padding: "2px 7px" }}>
                ✅ Basic (L1)
              </span>
            ) : null}
          </div>
        </div>
        <button
          onClick={disconnect}
          style={{
            background: "none", border: `1px solid ${cardBorder}`,
            borderRadius: 6, padding: "4px 10px",
            fontSize: 11, color: mutedColor, cursor: "pointer",
          }}
        >
          {lang === "en" ? "Remove" : "Poista"}
        </button>
      </div>
    );
  }

  return (
    <>
      {idOnly && (
        <div style={{
          background: "#fffbeb", border: "1px solid #fcd34d",
          borderRadius: 10, padding: "10px 14px", marginBottom: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
                ⚠️ {lang === "en" ? "DUPR ID entered, account not connected" : "Tunnus syötetty, tiliä ei ole yhdistetty"}
              </div>
              <div style={{ fontSize: 11, color: "#92400e", marginTop: 2, fontFamily: "monospace", letterSpacing: 2 }}>{current?.duprId}</div>
            </div>
            <button onClick={disconnect} style={{
              background: "none", border: "1px solid #fcd34d", borderRadius: 6,
              padding: "4px 10px", fontSize: 11, color: "#92400e", cursor: "pointer", whiteSpace: "nowrap",
            }}>
              {lang === "en" ? "Remove" : "Poista"}
            </button>
          </div>
          <div style={{ fontSize: 11, color: "#92400e", marginTop: 6, lineHeight: 1.5 }}>
            {lang === "en"
              ? "Sign in to DUPR below and accept its terms. Until then DUPR does not release your rating and your matches are not sent to DUPR."
              : "Kirjaudu DUPR:iin alta ja hyväksy sen ehdot. Sitä ennen DUPR ei luovuta ratingiasi eivätkä ottelusi mene DUPR:iin."}
          </div>
        </div>
      )}
      <button
        onClick={() => { setError(null); setShowModal(true); }}
        style={{
          width: "100%",
          background: "linear-gradient(135deg, #1a56db, #163b68)",
          border: "none", borderRadius: 10,
          padding: "13px 16px",
          color: "#fff", fontSize: 14, fontWeight: 700,
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}
      >
        {lang === "en" ? "🔗 Connect DUPR Account" : "🔗 Yhdistä DUPR-tili"}
      </button>

      {showModal && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.75)",
          zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#fff", borderRadius: 12,
            width: "min(480px, 95vw)", height: "min(620px, 90vh)",
            display: "flex", flexDirection: "column",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          }}>
            {/* Header */}
            <div style={{
              padding: "14px 16px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              borderBottom: "1px solid #e5e7eb", flexShrink: 0,
            }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: "#163b68" }}>
                {lang === "en" ? "Connect DUPR Account" : "Yhdistä DUPR-tili"}
              </span>
              <button
                onClick={() => { setShowModal(false); setLoading(false); setError(null); }}
                style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#6b7280", lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            {/* Content */}
            {loading ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
                <div style={{ fontSize: 28 }}>⏳</div>
                <div style={{ fontSize: 14, color: "#6b7280" }}>{lang === "en" ? "Saving DUPR data..." : "Tallennetaan DUPR-tietoja..."}</div>
              </div>
            ) : error ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12, padding: 24 }}>
                <div style={{ fontSize: 28 }}>❌</div>
                <div style={{ fontSize: 13, color: "#dc2626", textAlign: "center" }}>{error}</div>
                <button
                  onClick={() => setError(null)}
                  style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid #d1d5db", cursor: "pointer", fontSize: 13 }}
                >
                  Yritä uudelleen
                </button>
              </div>
            ) : (
              <iframe
                src={iframeSrc}
                style={{ flex: 1, border: "none", width: "100%" }}
                title="DUPR kirjautuminen"
                allow="payment"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DuprConnectButton;
